<script setup>
const props = defineProps({
    baseButtonConfig: {
        type: Object,
        required: false,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                type: rawValue => typeof rawValue?.type === 'string' && ['submit', 'external', 'file', 'button', 'internal'].includes(rawValue?.type),
                buttonType: rawValue => typeof rawValue?.buttonType === 'string' && ['primary', 'secondary'].includes(rawValue?.buttonType),
                active: rawValue => rawValue?.active === undefined || typeof rawValue?.active === 'boolean',
                large: rawValue => rawValue?.large === undefined || typeof rawValue?.large === 'boolean',
                isPending: rawValue => rawValue?.isPending === undefined || typeof rawValue?.isPending === 'boolean',
                disable: rawValue => rawValue?.disable === undefined || typeof rawValue?.disable === 'boolean',
                link: rawValue => rawValue?.link === undefined || typeof rawValue?.link === 'string',
                center: rawValue => rawValue?.center === undefined || typeof rawValue?.center === 'boolean',
                query: rawValue => rawValue?.query === undefined || typeof rawValue?.query === 'string'
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

const isSubmitBtn = computed(() => props.baseButtonConfig?.type === 'submit')
const isExternalLink = computed(() => props.baseButtonConfig?.type === 'external')
const isButton = computed(() => props.baseButtonConfig?.type === 'button')
const target = computed(() => isExternalLink.value ? '_blank' : null)
const rel = computed(() => isExternalLink.value ? 'noopener noreferrer' : null)
const type = computed(() => isSubmitBtn.value ? 'submit' : null)

const buttonClass = computed(() => {
    return {
        'btn-primary': props.baseButtonConfig?.buttonType === 'primary',
        'btn-secondary': props.baseButtonConfig?.buttonType === 'secondary',
        'btn-center': !props.baseButtonConfig?.center,
        'btn-active': props.baseButtonConfig?.active,
        'btn-large': props.baseButtonConfig?.large,
        'btn-pending': props.baseButtonConfig?.isPending
    }
})

// Compute data attributes from a tracking array
const dataAttributes = computed(() => {
    if (!props.baseButtonConfig?.tracking) return {}

    const attributes = {}
    props.baseButtonConfig.tracking.forEach((obj) => {
        for (const [key, value] of Object.entries(obj)) {
            attributes[`data-${key}`] = value
        }
    })
    return attributes
})

const component = computed(() => isSubmitBtn.value ? 'button' : isButton.value ? 'button' : resolveComponent('NuxtLinkLocale'))

const getButtonLink = computed(() => {
    return props.baseButtonConfig?.query ? { name: props.baseButtonConfig?.link, query: { [props.baseButtonConfig?.query]: true } } : props.baseButtonConfig?.link
})
</script>

<template>
    <component
        :is="component"
        :to="getButtonLink"
        :class="buttonClass"
        :target="target"
        :rel="rel"
        :type="type"
        :disabled="baseButtonConfig?.disabled"
        v-bind="dataAttributes"
    >
        <span><slot /></span>
    </component>
</template>

<style lang="scss">
.btn-primary {
    @include btn($type: 'primary');
}

.btn-secondary {
    @include btn($type: 'secondary');
}
</style>
